// Vocabulaires du circuit documentaire (miroir de bio24-ged-data.js) :
// états + tons, types de documents, étapes de circuit, rôles GED.
import { ISO15189 } from '@/shared/iso15189';
import type { DocStatut } from '@/lib/types';

export interface StateMeta { key: string; label: string; tone: string; desc: string }

export const STATES: Record<DocStatut, StateMeta> = {
  demande: { key: 'demande', label: 'Demandé', tone: 'muted', desc: 'Demande ouverte, pas encore rédigée.' },
  brouillon: { key: 'brouillon', label: 'Brouillon', tone: 'muted', desc: 'Espace privé du rédacteur, versionné v0.x.' },
  averifier: { key: 'averifier', label: 'À vérifier', tone: 'info', desc: 'Soumis, contenu figé pour revue.' },
  aapprouver: { key: 'aapprouver', label: 'À approuver', tone: 'info', desc: 'Vérifié, en attente de signature.' },
  envigueur: { key: 'envigueur', label: 'En vigueur', tone: 'success', desc: 'Version applicable unique.' },
  enrevision: { key: 'enrevision', label: 'En révision', tone: 'warning', desc: 'Nouvelle version en préparation ; l’ancienne reste applicable.' },
  obsolete: { key: 'obsolete', label: 'Obsolète', tone: 'neutral', desc: 'Remplacé, conservé en lecture.' },
  retire: { key: 'retire', label: 'Retiré d’urgence', tone: 'danger', desc: 'Applicabilité gelée immédiatement.' },
  refuse: { key: 'refuse', label: 'Refusé', tone: 'neutral', desc: 'Demande refusée (état latéral tracé).' },
  abandonne: { key: 'abandonne', label: 'Abandonné', tone: 'neutral', desc: 'Brouillon abandonné (état latéral tracé).' },
  /* ── Document externe ──────────────────────────────────────────────────
     Trois états, et pas les mêmes mots que le circuit interne — parce que ce
     ne sont pas les mêmes actes. « En vigueur » supposerait que le laboratoire
     a signé le texte ; il ne l'a pas écrit, il déclare s'y référer. */
  enregistre: { key: 'enregistre', label: 'Enregistré', tone: 'muted', desc: 'Référencé au laboratoire ; n’engage encore rien.' },
  adopte: { key: 'adopte', label: 'Adopté', tone: 'success', desc: 'Le laboratoire déclare s’y référer — c’est l’édition contre laquelle il s’audite.' },
  remplace: { key: 'remplace', label: 'Remplacé', tone: 'neutral', desc: 'Une édition postérieure a été adoptée ; l’exemplaire reste consultable.' },
};

/** ton → couleurs pastilles (design system) */
export const TONES: Record<string, { bg: string; fg: string }> = {
  muted: { bg: 'var(--gray-50)', fg: 'var(--text-muted)' },
  info: { bg: 'var(--cyan-50)', fg: 'var(--cyan-700)' },
  success: { bg: 'var(--green-50)', fg: 'var(--green-600)' },
  warning: { bg: 'var(--amber-50)', fg: 'var(--amber-600)' },
  danger: { bg: 'var(--red-50)', fg: 'var(--red-600)' },
  neutral: { bg: 'var(--gray-50)', fg: 'var(--text-body)' },
};

/**
 * L'état d'un DOCUMENT, mis en forme.
 *
 * ⚠️ UN ÉTAT INCONNU S'ÉCRIT TEL QUEL (05/10/2026). Le repli était
 * `STATES.brouillon` : tout code hors de la table — y compris un état qui
 * n'appartient pas au circuit documentaire — s'affichait « Brouillon ». C'est
 * ainsi que « Mon planning » annonçait « Brouillon » sur un congé VALIDÉ
 * (`statut = 'validee'`, vocabulaire des absences). Le repli affirmait un état
 * au lieu de dire qu'il ne le connaissait pas.
 *
 * Les absences ont désormais leur propre vocabulaire (`@/lib/etats-absence`).
 * Ce repli-ci reste la seconde moitié du garde-fou : un code imprévu se LIT,
 * donc il se signale, au lieu de se déguiser en état plausible.
 */
export function stateMeta(statut: string): StateMeta & { bg: string; fg: string } {
  const connu = STATES[statut as DocStatut];
  const s = connu || { key: statut, label: statut || '—', tone: 'neutral', desc: 'État hors du circuit documentaire.' };
  const t = TONES[s.tone] || TONES.neutral;
  return { ...s, ...t };
}

export const CIRCUIT_STEPS = [
  { key: 'redaction', label: 'Rédaction', short: 'Réd.', locked: true },
  { key: 'verification', label: 'Vérification', short: 'Vér.' },
  { key: 'approbation', label: 'Approbation', short: 'App.', locked: true },
  { key: 'signature', label: 'Signature', short: 'Sig.' },
  { key: 'diffusion', label: 'Diffusion', short: 'Diff.' },
];

export const GED_ROLES: Record<string, { label: string; short: string }> = {
  /* LES MÉTIERS ET RÉFÉRENTS (refonte des droits, 16/10/2026) — mêmes codes que
     `backend/src/roles-acces.ts` (`ROLES_ACCES`). Ils remplacent les casquettes :
     un rôle de métier OUVRE des rubriques, c'est la grille du moteur qui dit
     lesquelles. Les libellés courts servent aux badges de l'organigramme. */
  accueil: { label: 'Accueil & prélèvement', short: 'ACC' },
  preana: { label: 'Pré-analytique', short: 'PRÉ' },
  tech: { label: 'Technicien plateau', short: 'TECH' },
  bio: { label: 'Biologiste', short: 'BIO' },
  compta: { label: 'Comptabilité', short: 'CPT' },
  direction: { label: 'Direction & revue', short: 'DIR' },
  reftech: { label: 'Référent plateau technique', short: 'R-TECH' },
  refpreana: { label: 'Référent pré-analytique', short: 'R-PRÉ' },
  rq: { label: 'Administrateur / RQ', short: 'RQ' },
  /* Coordinateur qualité (07/10/2026) : il ouvre le dossier d'une déclaration non
     retenue et mène le cycle d'une NC avec le RQ. ⚠️ Il ne la RETIENT plus et ne
     la rejette plus depuis le 13/10/2026 : c'est au RQ — ou à qui les rôles fins
     l'accordent (acte C13, « Actes réglables »).
     ⚠️ MIROIR de `backend/src/nc-retenue.ts` (`ROLES_REGISTRE`) et de
     `ROLES_VALIDES` côté paramétrage : les trois listes doivent bouger ensemble.
     ⚠️ Comme tout rôle ici, il n'OUVRE aucune rubrique — l'accès aux écrans se
     règle au paramétrage, par profil. */
  cq: { label: 'Coordinateur qualité', short: 'CQ' },
  /* Achats (10/10/2026) : tient les fournisseurs du carnet de contacts, rien d'autre. */
  achats: { label: 'Achats', short: 'ACH' },
  redacteur: { label: 'Rédacteur', short: 'RED' },
  verificateur: { label: 'Vérificateur', short: 'VÉR' },
  approbateur: { label: 'Approbateur', short: 'APP' },
  lecteur: { label: 'Lecteur', short: 'LEC' },
  auditeur: { label: 'Auditeur', short: 'AUD' },
  // Gestionnaire RH / paie (lot 0). Attribué NOMMÉMENT, jamais déduit du profil :
  // c'est lui — et lui seul — qui accède aux données de santé du personnel
  // (certificats, motifs d'absence détaillés). Un RQ qui porte aussi ce rôle y
  // accède À CE TITRE ; sa qualité de RQ n'y donne aucun droit.
  rh: { label: 'Gestionnaire RH / paie', short: 'RH' },
  // Responsable technique (05/10/2026) : se prononce sur les habilitations et la
  // formation, avec le RQ. Ne donne aucun accès à l'administratif RH.
  rt: { label: 'Responsable technique', short: 'RT' },
  // Métrologie (05/10/2026) : étalons, certificats de raccordement et saisie
  // des vérifications métrologiques, au même titre que le RQ. ANNULER une
  // vérification reste au RQ seul — c'est un constat daté que l'évaluateur lira.
  metro: { label: 'Métrologie', short: 'METRO' },
  /* ⚠️ `responsable` (« Responsable de service », RESP) A ÉTÉ SUPPRIMÉ le
     05/10/2026. Né au lot 3 pour valider les présences de son équipe, il a
     perdu ce droit le 04/10 (reporté au seul rôle `rh`) et n'ouvrait plus
     aucune rubrique ni aucun acte ; aucun compte ne le portait. Motifs et
     garde-fou : `backend/src/roles-droits.ts`.
     ⚠️ Ne pas le réintroduire ici sans lui donner une rubrique : un rôle
     affiché qui ne ferme rien fait croire qu'un droit est réparti. */
};

/**
 * LES RÔLES NOMINATIFS DE L'ORGANIGRAMME (`rh-personnel.rolesOrg`) — distincts
 * de `GED_ROLES` ci-dessus, qui commandent les DROITS documentaires.
 *
 * ⚠️ MIROIR EXACT de `backend/src/org-intitule.ts` (`ROLES_ORG`). Un intitulé
 * calculé affiche « Responsable qualité » à partir de cette même clé `rq` —
 * les deux listes doivent rester identiques, ou l'organigramme composerait un
 * texte que ce fichier ne sait plus proposer à la saisie.
 *
 * ⚠️ HOMONYMIE ASSUMÉE, ET DANGEREUSE : `rq` et `rh` désignent ICI un INTITULÉ
 * et là-haut (`GED_ROLES`) un DROIT. Les deux vivent sur la même fiche de
 * personne, à deux lignes d'écart. Nommer quelqu'un « Responsable qualité »
 * dans cette liste-ci ne lui ouvre RIEN.
 *
 * Les clés ne sont pas renommées et ne le seront pas : elles sont figées dans
 * le journal chaîné (2 331 acteurs dans les empreintes). Ce qui a été corrigé
 * le 10/09/2026, ce sont les LIBELLÉS DES DEUX BLOCS à l'écran — « Rôles
 * documentaires (droits) » et « Rôles d'organigramme (intitulé) » — plus une
 * phrase sous le second. Une homonymie qu'on ne peut pas supprimer se dit.
 */
export const ROLES_ORG: Record<string, string> = {
  direction: 'Co-direction',
  rt: 'Responsable technique',
  rq: 'Responsable qualité',
  rh: 'Gestion des ressources humaines',
};


/**
 * Les équipements qu'on peut DÉSIGNER dans un formulaire.
 *
 * Demande de la RQ le 14/08/2026, et elle vaut « pour toute l'application » :
 * les ACCESSOIRES n'ont pas à figurer dans un sélecteur d'équipement. Un
 * onduleur, un clavier, l'ordinateur d'un automate ne mesurent rien et ne se
 * rattachent à rien par eux-mêmes — ils sont déjà, implicitement, rattachés à
 * l'appareil qu'ils servent. Les proposer, c'est allonger une liste de 245
 * lignes avec 154 entrées qu'on ne choisira jamais, et prendre le risque qu'on
 * en choisisse une par erreur.
 *
 * ⚠️ `nature` absent vaut `mesure` : le champ a été ajouté sur un parc
 * existant, et traiter l'absence comme « accessoire » ferait disparaître des
 * appareils réels de tous les sélecteurs d'un coup.
 *
 * Ne filtre PAS sur le statut : un appareil hors service ou en maintenance
 * reste désignable — on documente une validation de méthode passée, une
 * intervention à venir, un dossier historique. C'est un autre débat que celui
 * des accessoires, et il se tranche écran par écran.
 */
export function equipementsDesignables<T extends { nature?: string }>(equips: T[]): T[] {
  return (equips || []).filter((e) => String(e.nature || 'mesure') !== 'accessoire');
}

export const PERIODICITES = [
  { key: 'annuelle', label: 'Annuelle', months: 12 },
  { key: 'biennale', label: 'Biennale', months: 24 },
  { key: 'triennale', label: 'Triennale', months: 36 },
];

// ---- SMQ (G2) — vocabulaires bio24-ged-data.js ----
export const NC_SOURCES = [
  // Déclaration express (gommette) : le déclarant décrit, il ne classe pas.
  // Un processus deviné rendrait la NC invisible dans la cartographie ; « à
  // qualifier » la rend au contraire visible ET traitable par le RQ.
  { key: 'aqualifier', label: 'À qualifier', aide: "Je signale sans classer : la RQ classera. Rien d'autre n'est exigé." },
  { key: 'preanalytique', label: 'Pré-analytique', aide: "Avant l'analyse : prélèvement, identification, tube, transport, réception." },
  { key: 'analytique', label: 'Analytique', aide: "Pendant l'analyse : passage sur l'automate, résultat aberrant." },
  { key: 'postanalytique', label: 'Post-analytique', aide: "Après l'analyse : validation, compte rendu, transmission du résultat." },
  { key: 'equipement', label: 'Équipement / métrologie', aide: "Panne, dérive ou écart d'étalonnage d'un appareil." },
  // Une évaluation externe hors limites est une non-conformité analytique, mais
  // elle se distingue : elle vient d'un organisme TIERS, arrive avec un mois de
  // retard sur les résultats concernés, et se compte à part en revue de direction.
  { key: 'eeq', label: 'Évaluation externe (EEQ)', aide: 'Résultat hors limites à une évaluation externe de la qualité.' },
  { key: 'cqi', label: 'Contrôle interne (CQI)', aide: 'Contrôle interne de qualité hors limites, ou règle de Westgard rejetée.' },
  { key: 'reactif', label: 'Réactif / consommable', aide: 'Réactif, calibrant ou consommable défectueux, périmé ou livré non conforme.' },
  { key: 'sil', label: 'SIL / informatique', aide: 'Panne ou erreur du système informatique du laboratoire.' },
  { key: 'environnement', label: 'Environnement / locaux', aide: 'Locaux, température, hygiène, sécurité.' },
  { key: 'rh', label: 'Ressources humaines', aide: 'Personnel : habilitation, formation, effectif.' },
  { key: 'documentaire', label: 'Documentaire', aide: 'Procédure absente, périmée ou non appliquée.' },
  { key: 'management', label: 'Management de la qualité', aide: 'Pilotage du système : revue de direction, audits, objectifs, risques, responsabilités.' },
  { key: 'reclamation', label: 'Réclamation client', aide: 'Insatisfaction exprimée par un patient, un préleveur ou un correspondant.' },
];
/**
 * SOURCE → PROCESSUS suggéré, pour PRÉ-REMPLIR le formulaire de déclaration.
 *
 * ⚠️ COPIE. L'original fait autorité : `backend/src/nc-declaration.ts`
 * (`PROCESSUS_SUGGERE`), qui est testé. Les deux tables sont comparées
 * caractère par caractère par `backend/tests/qualite-processus-suggere.test.js`
 * — une divergence fait échouer `npm test` plutôt que de produire, à l'écran,
 * une suggestion que le serveur refuse d'enregistrer.
 *
 * ⚠️ Les libellés sont ceux de la cartographie RÉELLE de BIO24, relevés en base
 * le 27/08/2026 — pas des noms plausibles. Un nom inventé serait rejeté par
 * `resolveProcessus` au moment d'enregistrer : proposer une valeur que le
 * serveur refuse est pire que ne rien proposer.
 */
export const PROCESSUS_SUGGERE: Record<string, string> = {
  preanalytique: 'Pré-analytique',
  analytique: 'Analytique',
  postanalytique: 'Post-analytique',
  equipement: 'Maintenance & étalonnage',
  cqi: 'Analytique',
  eeq: 'Analytique',
  reactif: 'Pré-analytique',
  rh: 'Gestion du personnel / RH',
  documentaire: 'Gestion des documents',
  management: 'Organisation générale & Pilotage',
  sil: 'Informatique & traçabilité',
  environnement: 'Hygiène, sécurité & environnement',
  reclamation: 'Surveillance & Amélioration du SMQ',
};

/** Le processus proposé pour une source — vide si aucune correspondance. */
export function processusSuggere(source: string): string {
  return PROCESSUS_SUGGERE[String(source || '').trim()] || '';
}

/**
 * Codes cause du registre de maintenance des équipements.
 *
 * Relevés dans la légende du gabarit papier du laboratoire (classeurs EQ01,
 * EQ09, EQ21). Ils répondent à « d'où vient la panne » — pas à « pourquoi le
 * système ne l'a pas empêchée », qui reste l'analyse de cause de la
 * non-conformité. Les confondre reviendrait à clore une NC sur « pièce
 * défectueuse », ce qui n'a jamais rien corrigé.
 */
/**
 * Vocabulaire du registre de calibration, repris de la feuille `BASE DE DONNES`
 * du classeur Cobas — c'est le laboratoire qui l'a écrit, pas nous.
 *
 * Il vit ici comme REPLI. Dès qu'un écran de paramétrage existera, ces listes
 * devront s'éditer : figer des causes en dur rendrait impossible d'en ajouter
 * une sans livraison, et le laboratoire en ajoute au fil des situations.
 */
export const CAL_RESULTATS: Record<string, { label: string; tone: string }> = {
  valide: { label: 'Validée', tone: 'success' },
  non_valide: { label: 'Non validée', tone: 'danger' },
};
export const CAL_TYPES: Record<string, { label: string; aide: string }> = {
  LOT: { label: 'Lot', aide: 'Nouveau lot de réactif — la calibration engage tous les résultats produits avec ce lot.' },
  PACK: { label: 'Pack', aide: 'Nouveau conditionnement du même lot — portée plus limitée.' },
};
export const CAL_CAUSES = [
  'Nouveau lot', 'Nouveau réactif', 'Reciblage contrôle',
  'Vérification résultat', 'Linéarité de la courbe', 'Réactif inversé', 'Maintenance', 'Autre',
];

/**
 * Bornes d'acceptabilité du facteur de calibration.
 *
 * PARAMÉTRABLES, jamais codées en dur : le laboratoire ne les a pas encore
 * fixées (question posée le 29/07). Tant qu'elles valent `null`, l'outil
 * ENREGISTRE sans juger — il ne refuse rien qu'on ne lui ait demandé de
 * refuser. Repère observé sur les 625 facteurs repris : médiane exactement
 * 1,000, étendue 0,432 → 2,55, sept valeurs hors [0,7 ; 1,3].
 */
export const CAL_FACTEUR_BORNES: { min: number | null; max: number | null } = { min: null, max: null };

export const CAUSES_PANNE: Record<string, string> = {
  H: 'Humaine',
  F: 'Fonctionnelle (pièce défectueuse)',
  M: 'Maintenance insuffisante',
  S: 'Software / mise à jour',
};
export const NC_GRAVITES = [
  { key: 'mineure', label: 'Mineure', tone: 'info' },
  { key: 'majeure', label: 'Majeure', tone: 'warning' },
  { key: 'critique', label: 'Critique', tone: 'danger' },
];
/**
 * Des résultats patients ont-ils été touchés ? (§7.5 pour les NC, §8.6 pour les
 * réclamations.)
 *
 * C'est la seule question dont la réponse peut obliger à rappeler des comptes
 * rendus déjà partis. « Je ne sais pas encore » est une réponse RECEVABLE et
 * c'est le défaut : au moment où l'on déclare, on l'ignore presque toujours.
 * Ce qui ne l'est pas, c'est de clore sans y avoir jamais répondu — un défaut à
 * « non » ferait répondre l'outil à la place du déclarant, et personne ne
 * reviendrait sur une case qui paraît déjà remplie.
 */
export const IMPACTS_RESULTATS = [
  { key: 'oui', label: 'Oui', tone: 'danger', aide: 'Des résultats ont été rendus sous le coup de cette non-conformité.' },
  { key: 'non', label: 'Non', tone: 'success', aide: 'Détectée avant tout rendu de résultat.' },
  { key: 'inconnu', label: 'Je ne sais pas encore', tone: 'warning', aide: 'À préciser pendant le traitement — la case ne peut pas rester ainsi à la clôture.' },
].map((o) => ({ ...o, ...(TONES[o.tone] || TONES.neutral) }));

/* Les libellés de TOUS les types, anciens compris : une fiche qui porte encore
   « Réclamation » doit se lire. Les choix PROPOSÉS sont `NC_TYPES_SAISIE`. */
export const NC_TYPES = [
  /* « Qui a relevé l'écart ? » (RQ, 13/10/2026) — le mot « Type » ne disait rien.
     Les CLÉS ne changent pas : elles sont en base, dans l'export et au moteur. */
  { key: 'interne', label: 'Le laboratoire' },
  { key: 'externe', label: 'Un intervenant extérieur (audit, EEQ, correspondant, patient)' },
  { key: 'reclamation', label: 'Réclamation (ancienne valeur)' },
];

/**
 * LES SOURCES SELON QUI A RELEVÉ L'ÉCART (RQ, 13/10/2026).
 *
 * La liste « Source » mêle des SECTEURS (pré-analytique, équipement, RH…), que
 * le laboratoire comme l'extérieur peuvent mettre en cause, et des CANAUX qui
 * n'appartiennent qu'à l'un des deux : le contrôle interne au laboratoire,
 * l'évaluation externe et la réclamation à l'extérieur. Seuls ces canaux sont
 * filtrés — un auditeur peut relever un écart documentaire comme un technicien.
 */
const SOURCE_RESERVEE: Record<string, 'interne' | 'externe'> = {
  cqi: 'interne', eeq: 'externe', reclamation: 'externe',
};
/** Les sources proposées quand l'écart a été relevé par `releve`. */
export function sourcesPour(releve: string) {
  return NC_SOURCES.filter((s) => !SOURCE_RESERVEE[s.key] || SOURCE_RESERVEE[s.key] === releve);
}
/**
 * COMMENT L'ÉCART A-T-IL ÉTÉ DÉTECTÉ ? — le CANAL (RQ, 13/10/2026).
 *
 * Séparé de la « source », qui dit désormais le SECTEUR mis en cause. Chaque
 * canal n'est relevé que d'un côté : la liste se filtre par « qui a relevé ».
 * ⚠️ MIROIR de `NC_CANAUX` du moteur (`services/vocabulaire-nc.ts`) — mêmes
 * clés, même côté ; `qualite-nc-canal.test.js` les compare.
 */
export const NC_CANAUX: { key: string; label: string; releve: 'interne' | 'externe'; secteur?: string }[] = [
  { key: 'observation', label: 'Constat au poste, en routine', releve: 'interne' },
  { key: 'cqi', label: 'Contrôle interne de qualité (CQI)', releve: 'interne', secteur: 'cqi' },
  { key: 'audit_interne', label: 'Audit interne', releve: 'interne' },
  { key: 'revue', label: 'Revue, indicateur ou réunion qualité', releve: 'interne' },
  { key: 'eeq', label: 'Évaluation externe de la qualité (EEQ)', releve: 'externe', secteur: 'eeq' },
  { key: 'audit_externe', label: 'Audit externe ou évaluation d’accréditation', releve: 'externe' },
  { key: 'reclamation', label: 'Réclamation d’un patient ou d’un prescripteur', releve: 'externe', secteur: 'reclamation' },
  { key: 'correspondant', label: 'Signalement d’un correspondant ou d’un prestataire', releve: 'externe' },
];
/** Les canaux proposés quand l'écart a été relevé par `releve`. */
export const canauxPour = (releve: string) => NC_CANAUX.filter((c) => c.releve === releve);
/** Le libellé d'un canal — la clé si elle est inconnue, rien si vide. */
export const libelleCanal = (cle?: string | null) => (cle ? NC_CANAUX.find((c) => c.key === cle)?.label || cle : '');

/**
 * DE QUOI S'AGIT-IL ? — LA NATURE DE LA NC (RQ, 13/10/2026).
 *
 * La PREMIÈRE question de la déclaration. Elle commande :
 *  · les SECTEURS dont la liste « Quel problème ? » est tirée ;
 *  · le PROCESSUS concerné proposé (par son code de cartographie) ;
 *  · les OBJETS à relier (l'équipement en panne, le lot, le document…) ;
 *  · les champs propres (correspondant, rubriques de panne).
 * ⚠️ MIROIR des clés de `NC_NATURES` du moteur (`services/vocabulaire-nc.ts`),
 * avec le même secteur en tête de `secteurs` ; `qualite-nc-nature.test.js`
 * les compare. `aqualifier` = « Je ne sais pas » : le RQ classera.
 */
export type ObjetNC = 'equipement' | 'produit' | 'lot' | 'fournisseur' | 'analyte' | 'document' | 'audit' | 'emplacement';
export const NC_NATURES: {
  key: string; label: string; aide: string; secteurs: string[]; codif: string; objets: ObjetNC[]; correspondant?: boolean;
}[] = [
  { key: 'demande', label: 'Demande ou échantillon', aide: 'Prescription, identification, tube, transport, réception.', secteurs: ['preanalytique'], codif: 'PRE', objets: [], correspondant: true },
  { key: 'resultat', label: 'Résultat d’analyse', aide: 'Contrôle interne, évaluation externe, résultat aberrant.', secteurs: ['analytique', 'cqi', 'eeq'], codif: 'ANA', objets: ['analyte', 'equipement', 'produit', 'lot'], correspondant: true },
  { key: 'compte_rendu', label: 'Compte rendu, transmission', aide: 'Validation, compte rendu, transmission au prescripteur.', secteurs: ['postanalytique'], codif: 'POST', objets: [], correspondant: true },
  { key: 'equipement', label: 'Équipement', aide: 'Panne, dérive, métrologie, température d’enceinte.', secteurs: ['equipement'], codif: 'MAIN', objets: ['equipement'] },
  { key: 'reactif', label: 'Réactif, consommable, fournisseur', aide: 'Lot défectueux, péremption, livraison non conforme.', secteurs: ['reactif'], codif: 'ACH', objets: ['produit', 'lot', 'fournisseur'] },
  { key: 'locaux', label: 'Locaux, hygiène, sécurité', aide: 'Température d’un local, nettoyage, déchets, accident.', secteurs: ['environnement'], codif: 'HYG', objets: ['emplacement'] },
  { key: 'personnel', label: 'Personnel, habilitation', aide: 'Poste tenu sans habilitation, effectif, formation.', secteurs: ['rh'], codif: 'RH', objets: [] },
  { key: 'document', label: 'Document, informatique', aide: 'Procédure absente ou périmée, SIL, sauvegarde.', secteurs: ['documentaire', 'sil'], codif: 'DOC', objets: ['document'] },
  { key: 'management', label: 'Système de management', aide: 'Audit, revue de direction, objectifs, risques.', secteurs: ['management'], codif: 'ORG', objets: ['audit'] },
  { key: 'aqualifier', label: 'Je ne sais pas', aide: 'Le responsable qualité classera.', secteurs: [], codif: '', objets: [] },
];
/* L'ANCIENNE NATURE « réclamation », GARDÉE (RQ, 13/10/2026) : une NC rangée en
   réclamation la conserve et la montre ; elle n'est plus proposée en tuile — une
   réclamation est une façon dont l'écart arrive, pas ce qu'il est. */
const NATURES_HISTORIQUES: typeof NC_NATURES = [
  { key: 'reclamation', label: 'Réclamation (ancienne valeur)', aide: 'Valeur des NC d’avant la nature.', secteurs: ['reclamation'], codif: 'SMQ', objets: [] },
];
export const natureMeta = (cle?: string | null) => NC_NATURES.find((n) => n.key === cle) || NATURES_HISTORIQUES.find((n) => n.key === cle);

/** La nature d'une NC d'AVANT la nature : déduite de son secteur (`source`). */
const NATURE_DU_SECTEUR: Record<string, string> = {
  preanalytique: 'demande', analytique: 'resultat', cqi: 'resultat', eeq: 'resultat', postanalytique: 'compte_rendu',
  equipement: 'equipement', reactif: 'reactif', environnement: 'locaux', rh: 'personnel', documentaire: 'document',
  sil: 'document', management: 'management', aqualifier: 'aqualifier', reclamation: 'reclamation',
};
/** La nature EFFECTIVE d'une NC : la sienne, sinon celle de son secteur ; `''` pour une réclamation ancienne (à préciser). */
export function natureDe(nc: { nature?: string | null; source?: string | null }): string {
  return String(nc.nature || '') || NATURE_DU_SECTEUR[String(nc.source || '')] || '';
}
/** Le SECTEUR (`source`) que produit une nature — le canal précise le résultat (CQI, EEQ). */
export function secteurDeNature(nature: string, canal: string): string {
  if (nature === 'resultat' && (canal === 'cqi' || canal === 'eeq')) return canal;
  return natureMeta(nature)?.secteurs[0] || 'aqualifier';
}
/** Le processus de la cartographie qu'une nature PROPOSE (par son code), `''` sinon. */
export function processusDeNature(nature: string, procs: readonly { name: string; codif?: string }[]): string {
  const codif = natureMeta(nature)?.codif;
  return codif ? procs.find((p) => String(p.codif || '').toUpperCase() === codif)?.name || '' : '';
}
/**
 * LE PROCESSUS À L'ORIGINE DE LA DÉTECTION, CALCULÉ (RQ, 13/10/2026) — modifiable
 * en « Prise en charge ». Ce qui arrive par le système qualité (réclamation,
 * EEQ, audit, revue) est détecté par le SMQ ; un contrôle interne par
 * l'analytique ; un constat au poste par le processus où l'on travaille, c'est-
 * à-dire, à défaut de mieux, celui que l'écart concerne.
 */
export function processusDetectionDe(canal: string, processusConcerne: string, procs: readonly { name: string; codif?: string }[]): string {
  const parCodif = (c: string) => procs.find((p) => String(p.codif || '').toUpperCase() === c)?.name || '';
  if (['reclamation', 'eeq', 'audit_interne', 'audit_externe', 'revue'].includes(canal)) return processusSMQ(procs);
  if (canal === 'cqi') return parCodif('ANA');
  return processusConcerne;
}

/**
 * LE SECTEUR N'EST PLUS DEMANDÉ, IL SE DÉDUIT (RQ, 13/10/2026 : « à la place du
 * secteur concerné, le processus à l'origine de la détection et le processus
 * concerné »).
 *
 * Le champ `source` reste en base — la revue de direction compte « par source »,
 * l'assistant n'instruit que les NC « à qualifier », la fiche de vie lit les
 * pannes — mais il se CALCULE : d'après le canal quand c'est lui qui le dit
 * (CQI, EEQ, réclamation), sinon d'après le processus CONCERNÉ, par son CODE de
 * cartographie (un nom se renomme, un code non). Sans processus concerné, la NC
 * est « à qualifier » : le RQ la classera.
 */
export const SECTEUR_PAR_CODIF: Record<string, string> = {
  ORG: 'management', SMQ: 'management', PRE: 'preanalytique', ANA: 'analytique', POST: 'postanalytique',
  RH: 'rh', DOC: 'documentaire', INFO: 'sil', MAIN: 'equipement', ACH: 'reactif', HYG: 'environnement', ADM: 'management',
};
const SECTEUR_DU_CANAL: Record<string, string> = { cqi: 'cqi', eeq: 'eeq', reclamation: 'reclamation' };
export function secteurDe(canal: string, processus: string, procs: readonly { name: string; codif?: string }[]): string {
  if (!String(processus || '').trim()) return 'aqualifier';
  if (SECTEUR_DU_CANAL[canal]) return SECTEUR_DU_CANAL[canal];
  const codif = String(procs.find((p) => p.name === processus)?.codif || '').toUpperCase();
  if (SECTEUR_PAR_CODIF[codif]) return SECTEUR_PAR_CODIF[codif];
  return Object.keys(PROCESSUS_SUGGERE).find((k) => PROCESSUS_SUGGERE[k] === processus) || 'aqualifier';
}
/** Le processus SMQ de la cartographie — celui qui détecte une réclamation. Par code, puis par nom. */
export function processusSMQ(procs: readonly { name: string; codif?: string }[]): string {
  return (procs.find((p) => String(p.codif || '').toUpperCase() === 'SMQ') || procs.find((p) => /\bSMQ\b/.test(p.name)))?.name || '';
}

/** Le « qui a relevé » qu'une source IMPOSE — `null` si elle convient aux deux. */
export function releveImpose(source: string): 'interne' | 'externe' | null {
  return SOURCE_RESERVEE[String(source || '')] || null;
}
/** Les types proposés à la saisie (13/10/2026) : « Réclamation » doublait la
 *  source « Réclamation client ». MIROIR de `NC_TYPES` du moteur
 *  (`services/vocabulaire-nc.ts`). */
export const NC_TYPES_SAISIE = NC_TYPES.filter((t) => t.key !== 'reclamation');
export const NC_STATES: Record<string, { label: string; tone: string }> = {
  /* SOUMISE — l'étape de triage (27/08/2026). La déclaration est arrivée, elle
     n'est pas encore un écart : la RQ ou le pilote du processus visé la retient
     ou la rejette d'office. Ton d'alerte parce qu'elle attend quelqu'un ; une
     déclaration qui dort n'est pas un état neutre. */
  soumise: { label: 'Soumise — à trancher', tone: 'warning' },
  declaree: { label: 'Déclarée', tone: 'muted' },
  analyse: { label: 'En analyse', tone: 'info' },
  traitement: { label: 'En traitement', tone: 'warning' },
  cloturee: { label: 'Clôturée', tone: 'success' },
  /* ⚠️ `annulee` N'EST PLUS PRODUIT depuis le 30/08/2026 : l'annulation a été
     remplacée par la suppression totale (décision de la RQ). Le libellé RESTE
     pour les quatre dossiers annulés avant ce jour — les retirer du vocabulaire
     les afficherait sous leur code brut, ce qui ferait passer une décision
     ancienne pour un défaut d'écran. */
  annulee: { label: 'Annulée', tone: 'muted' },
};
export const ACTION_TYPES = [
  { key: 'corrective', label: 'Corrective' },
  { key: 'preventive', label: 'Préventive' },
  { key: 'amelioration', label: 'Amélioration' },
];
export const ACTION_ORIGINS = [
  { key: 'nc', label: 'Non-conformité' },
  { key: 'audit', label: 'Audit' },
  { key: 'revue', label: 'Revue de direction' },
  { key: 'reclamation', label: 'Réclamation' },
  { key: 'eeq', label: 'EEQ — évaluation externe' },
  { key: 'autre', label: 'Autre' },
];
export const ACTION_STATES: Record<string, { label: string; tone: string }> = {
  ouverte: { label: 'Ouverte', tone: 'muted' },
  encours: { label: 'En cours', tone: 'info' },
  realisee: { label: 'Réalisée', tone: 'warning' },
  verifiee: { label: 'Efficacité vérifiée', tone: 'success' },
  // Inscrite au plan par erreur. Sortie des compteurs SANS prétendre avoir été
  // menée — c'est ce qui la distingue d'une action réalisée.
  annulee: { label: 'Annulée', tone: 'muted' },
};
/** Statuts d'action corrective qui empêchent la clôture de la NC d'origine
 *  (ISO 15189 §8.7.2). Seuil retenu : « réalisée » suffit — la vérification
 *  d'efficacité peut suivre la clôture, la réalisation non.
 *  Le serveur applique la MÊME liste (`AC_BLOQUANTES`, backend
 *  src/api/ged/controllers/ged.ts) et refuse la transition : ici on ne fait
 *  qu'expliquer le blocage avant le clic. Les deux listes bougent ensemble. */
export const AC_BLOQUANTES = ['ouverte', 'encours'];
/** Longueur minimale du motif de dérogation RQ, alignée sur le serveur. */
export const MOTIF_DEROGATION_MIN = 20;
/* ---- Liens typés entre entités qualité --------------------------------- */
/** Le type de lien n'est pas décoratif : « causée par un lot » et « concerne un
 *  document » ne se comptent pas ensemble. Sans lui, on obtient un nuage de
 *  rattachements dont on ne peut tirer aucun indicateur. Volontairement peu
 *  nombreux — quatre sens suffisent à couvrir les cas réels. */
export const TYPES_LIEN: Record<string, { label: string; inverse: string; aide: string }> = {
  concerne: { label: 'concerne', inverse: 'concerné par', aide: 'Rattachement neutre : les deux objets se rapportent l’un à l’autre, sans relation de cause ni de réponse.' },
  cause: { label: 'a pour cause', inverse: 'a causé', aide: 'Imputation : l’objet lié est à l’origine du problème. C’est ce lien qui permet de compter les NC provoquées par un lot ou un équipement.' },
  traite: { label: 'traite', inverse: 'traité par', aide: 'Réponse : l’objet lié répond au problème (action corrective traitant une non-conformité).' },
  remplace: { label: 'remplace', inverse: 'remplacé par', aide: 'Succession : l’objet lié prend la place du précédent.' },
  // Seul type ORIENTÉ ET CONTRAINT : la source doit être un équipement
  // ACCESSOIRE, la cible un équipement DE MESURE. Le serveur refuse l'inverse —
  // sans quoi deux automates finiraient accessoires l'un de l'autre et le sens
  // du lien ne voudrait plus rien dire. C'est aussi ce qui permet de répondre à
  // « quels accessoires cette panne immobilise-t-elle ? » sans deviner le sens.
  accessoire_de: {
    label: 'accessoire de', inverse: 'a pour accessoire',
    aide: 'Rattachement d’un accessoire à l’appareil qu’il sert : l’ordinateur de l’INDIKO, l’onduleur du Cobas. Ne relie que deux équipements, d’un accessoire vers un équipement de mesure — et un accessoire ne sert qu’un appareil à la fois.',
  },
};
/** Entités liables — mêmes clés que côté serveur (`ENTITES`, ged.ts). */
export const ENTITES_LIABLES: Record<string, { label: string; vue: string }> = {
  nc: { label: 'Non-conformité', vue: 'nc' },
  action: { label: 'Action corrective', vue: 'actions' },
  document: { label: 'Document', vue: 'list' },
  equipement: { label: 'Équipement', vue: 'equip' },
  /* Le PRODUIT du catalogue (16/10/2026) — l'article, avant le lot : une NC
     « réactif » le choisit d'abord, et c'est lui qui restreint lots et fournisseur. */
  produit: { label: 'Produit du catalogue', vue: 'produits' },
  lot: { label: 'Réactif / lot', vue: 'fourn' },
  reclamation: { label: 'Réclamation', vue: 'recl' },
  audit: { label: 'Audit', vue: 'audits' },
  // Le fournisseur vit au carnet de contacts depuis L1 (10/10/2026).
  fournisseur: { label: 'Fournisseur', vue: 'correspondants' },
  // L'analyte est le rattachement le plus utile du registre : « la ferritine a
  // dérivé trois fois cette année » ne se lit qu'en reliant NC, EEQ et actions
  // au même paramètre. Sans lui, chaque incident reste isolé sur son automate.
  analyte: { label: 'Analyte / paramètre', vue: 'cqi' },
  eeq: { label: 'Résultat d’EEQ', vue: 'eeq' },
  rdd: { label: 'Revue de direction', vue: 'rdd' },
  // Le local du plan du laboratoire (13/10/2026) — il vit au paramétrage.
  emplacement: { label: 'Local', vue: 'settings' },
};

export const REC_CANAUX = [
  { key: 'patient', label: 'Patient' },
  { key: 'prescripteur', label: 'Prescripteur' },
  { key: 'clinique', label: 'Clinique / hôpital' },
  { key: 'autre', label: 'Autre' },
];
export const REC_STATES: Record<string, { label: string; tone: string }> = {
  recue: { label: 'Reçue', tone: 'muted' },
  analyse: { label: 'En analyse', tone: 'info' },
  reponse: { label: 'Réponse émise', tone: 'warning' },
  cloturee: { label: 'Clôturée', tone: 'success' },
};

/** pastille depuis un vocabulaire {label,tone} */
export function vocabMeta(map: Record<string, { label: string; tone: string }>, key: string) {
  const v = map[key] || { label: key, tone: 'neutral' };
  const t = TONES[v.tone] || TONES.neutral;
  return { ...v, ...t };
}
export function graviteMeta(key: string) {
  const g = NC_GRAVITES.find((x) => x.key === key) || NC_GRAVITES[0];
  const t = TONES[g.tone] || TONES.neutral;
  return { ...g, ...t };
}

/**
 * ⚠️ BARRES OBLIQUES, ET NON POINTS (arbitrage d'Inès du 27/08/2026, SYS-9).
 *
 * L'application écrivait `26.08.2026` partout, et les champs de saisie de date
 * — `<input type="date">` — affichaient `26/08/2026`. Deux formats sur le même
 * écran, l'un pour lire, l'autre pour saisir.
 *
 * ⚠️ Le champ natif N'EST PAS PILOTABLE : son format suit la langue du système
 * d'exploitation, pas la feuille de style. Le seul moyen d'avoir un format
 * unique était donc d'aligner l'application sur lui — l'inverse était
 * impossible, et un lecteur de dates ne devrait pas avoir à se demander si le
 * séparateur change de sens selon l'endroit.
 *
 * Une seule fonction rend toutes les dates lues de la plateforme : le
 * changement tient en un caractère, et c'est précisément pourquoi il valait la
 * peine d'être fait plutôt que discuté.
 */
export const fmtDate = (iso?: string | null) => {
  if (!iso) return '—';
  const p = String(iso).slice(0, 10).split('-');
  return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : String(iso);
};
export { jourIso, isoJour, aujourdhuiIso, fmtDateHeure } from './dates';
export const fmtDateTime = (iso?: string | null) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return `${fmtDate(iso)} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

/**
 * Une durée en HEURES ET MINUTES — « 1 h 18 », jamais « 1,3 h ».
 *
 * Retour d'Inès (25/08/2026) : un décimal ne se lit pas. Personne ne convertit
 * 0,3 h en minutes de tête, et l'erreur silencieuse (0,3 lu « 30 minutes »)
 * fausse une discussion sur des heures opposables à un salarié.
 *
 * ⚠️ MIROIR de `heuresLisibles` dans `backend/src/rh-pointage.ts`, où la
 * fonction est couverte par `tests/rh-pointage.test.js` (9 cas, dont
 * « 8,999 h → 9 h et jamais 8 h 60 »). Les deux dépôts ne partagent pas de
 * code : si l'une bouge, bouger l'autre.
 *
 * ⚠️ L'arrondi se fait sur les MINUTES, une seule fois — arrondir les heures
 * puis en tirer les minutes ferait apparaître « 8 h 60 ».
 */
export const heuresLisibles = (n: number | null | undefined): string => {
  if (n == null || !Number.isFinite(Number(n))) return '—';
  const total = Math.round(Math.abs(Number(n)) * 60);
  const h = Math.floor(total / 60);
  const min = total % 60;
  const signe = Number(n) < 0 ? '−' : '';
  if (!h) return `${signe}${min} min`;
  return min ? `${signe}${h} h ${String(min).padStart(2, '0')}` : `${signe}${h} h`;
};

// ---- Organigramme (niveaux) & cartographie des processus (éditables) ----
// Niveaux de l'organigramme BIO24-A-ORG-01/02 : Direction (co-gérance /
// biologistes associés) · SMQ · chaîne technique · référents de secteur.
/**
 * Niveaux de lecture de l'organigramme. `chaine` et `support` sont apparus avec
 * BIO24-A-ORG-01/02 V02 : sans eux, les 9 personnes de la chaîne technique et
 * de la comptabilité retombaient toutes dans « technique » via `tierOfUser`,
 * et l'organigramme affichait un encadrement pléthorique sans opérateurs.
 */
export const ORG_TIERS = [
  { key: 'direction', label: 'Direction / Biologistes', color: 'var(--navy-900)' },
  { key: 'qualite', label: 'Système de management de la qualité', color: 'var(--cyan-600)' },
  { key: 'technique', label: 'Encadrement technique', color: 'var(--blue-600)' },
  { key: 'chaine', label: 'Chaîne technique', color: 'var(--blue-600)' },
  { key: 'support', label: 'Fonctions support', color: 'var(--text-muted)' },
];
/**
 * Une personne peut-elle encore recevoir une responsabilité ?
 * Un compte suspendu (absence longue) ou archivé (départ) reste dans
 * l'organigramme — les documents le référencent — mais ne doit plus être
 * proposé pour une affectation à venir.
 */
export function estAffectable(u: { compte?: string; statut?: string }): boolean {
  // DEUX états, pas un. Le compte ne dit rien des deux tiers du personnel, qui
  // n'en a pas : leur seul état est celui du DOSSIER (`statut`). Ne regarder que
  // le compte rendait « affectable » un agent d'accueil suspendu la veille — il
  // restait proposé comme successeur dans un transfert et comme destinataire de
  // diffusion, sans que rien ne le signale.
  if (u.statut && u.statut !== 'actif') return false;
  return !u.compte || u.compte === 'active';
}
/** Libellé du cycle de vie du compte, pour l'organigramme. */
export const COMPTE_LABEL: Record<string, string> = { suspended: 'Suspendu', archived: 'Archivé' };
/** Libellé du statut de DOSSIER — le seul dont dispose le personnel sans compte. */
export const STATUT_LABEL: Record<string, string> = { suspendu: 'Suspendu', sorti: 'Sorti des effectifs' };
/** Ce qu'il faut afficher sur la vignette d'une personne hors service. */
export const libelleIndisponible = (u: { compte?: string; statut?: string }): string =>
  COMPTE_LABEL[u.compte || ''] || STATUT_LABEL[u.statut || ''] || 'Inactif';

/** Niveau d'un membre : explicite (orgTier) sinon dérivé de la fonction (rétro-compat). */
export function tierOfUser(u: { fonction?: string; orgTier?: string }): string {
  if (u.orgTier) return u.orgTier;
  const f = (u.fonction || '').toLowerCase();
  if (f.includes('direction') || f.includes('biologiste')) return 'direction';
  if (f.includes('qualité') || f.includes('qualite')) return 'qualite';
  return 'technique';
}

export const PROCESS_GROUPS = [
  { key: 'management', title: 'Processus de management', color: 'var(--navy-900)' },
  { key: 'realisation', title: 'Processus de réalisation', color: 'var(--cyan-600)' },
  { key: 'support', title: 'Processus support', color: 'var(--blue-600)' },
];
export interface GedProcessus {
  id: string; name: string; group: string; active: boolean;
  /** @deprecated Ancien code A→L de l'organigramme. Un processus portait DEUX
   *  codes — celui-ci et `codif` — ce qui était la source de la confusion :
   *  lequel fait foi ? Décision du 2026-07-27 : un seul code, libre, celui que
   *  le laboratoire choisit (`codif`). Ce champ n'est plus ni écrit ni affiché ;
   *  il n'est conservé dans le type que pour ne pas casser la lecture des
   *  paramétrages déjà en base. */
  code?: string;
  /** abréviation courte (ex. PRE, MET) portée dans la référence documentaire —
   *  distincte du code A→L, qui reste le repère de l'organigramme */
  codif?: string;
  /** id du pilote nommé (= GedUser.id) — ISO 15189:2022 §4 / §5.2 */
  pilote?: string;
  /** mention portée sur l'organigramme (ex. « Pilote à confirmer ») */
  note?: string;
}
/** Abréviation de codification d'un processus : celle saisie, sinon dérivée du nom. */
export function procCodif(p: { name: string; codif?: string }): string {
  return (p.codif && p.codif.trim()) ? p.codif.trim().toUpperCase() : autoDiscCode(p.name).slice(0, 3);
}

/**
 * Comparaison de processus tolérante à la casse, aux accents et aux espaces.
 * La cartographie comparait avec `===` sur une valeur saisie ailleurs : au
 * moindre écart d'un caractère, la NC n'était comptée nulle part — sans que
 * rien ne le signale (constat QA v2 BUG-03). La contrainte à la source (menu
 * alimenté par CETTE taxonomie) reste la vraie garde ; ceci couvre l'historique.
 */
export const normProc = (s: string) =>
  (s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/\s+/g, ' ').trim();
export const sameProc = (a: string, b: string) => !!a && !!b && normProc(a) === normProc(b);
// Cartographie réelle du laboratoire : 12 processus A→L (organigramme
// BIO24-A-ORG-01/02 V01). Les pilotes sont posés en base par la migration
// `applyOrgChart` du backend — ce repli sert quand le paramétrage n'est pas
// encore chargé. NB : ce sont des PROCESSUS, pas des disciplines (Biochimie,
// Hématologie… restent la taxonomie « Disciplines » du paramétrage).
/** Cartographie proposée au premier démarrage. Volontairement SANS code : la
 *  codification appartient au laboratoire (une lettre, trois lettres, un
 *  numéro — son choix), et l'ordre de cette liste fait l'ordre d'affichage.
 *  Un code imposé par défaut se retrouvait recopié tel quel faute d'être
 *  présenté comme un choix. */
export const DEFAULT_PROCESSUS: GedProcessus[] = [
  { id: 'p_a', name: 'Organisation générale & Pilotage', group: 'management', active: true },
  { id: 'p_b', name: 'Surveillance & Amélioration du SMQ', group: 'management', active: true },
  { id: 'p_c', name: 'Pré-analytique', group: 'realisation', active: true },
  { id: 'p_d', name: 'Analytique', group: 'realisation', active: true },
  { id: 'p_e', name: 'Post-analytique', group: 'realisation', active: true },
  { id: 'p_f', name: 'Gestion du personnel / RH', group: 'support', active: true },
  { id: 'p_g', name: 'Gestion des documents', group: 'support', active: true },
  { id: 'p_h', name: 'Informatique & traçabilité', group: 'support', active: true },
  { id: 'p_i', name: 'Maintenance & étalonnage', group: 'support', active: true },
  { id: 'p_j', name: 'Maîtrise des achats', group: 'support', active: true },
  { id: 'p_k', name: 'Hygiène, sécurité & environnement', group: 'support', active: true },
  { id: 'p_l', name: 'Maîtrise des processus administratifs', group: 'support', active: true },
];

/** Abréviation auto-dérivée d'un libellé (repli si aucun code n'est défini). */
export function autoDiscCode(discipline: string): string {
  return (discipline || 'GEN').toUpperCase().replace(/[ÀÂÄ]/g, 'A').replace(/[ÉÈÊË]/g, 'E').replace(/[ÎÏ]/g, 'I').replace(/[ÔÖ]/g, 'O').replace(/[ÙÛÜ]/g, 'U').replace(/[^A-Z0-9]/g, '').slice(0, 8) || 'GEN';
}

// ---- Codification des références documentaires (composable, Paramétrage Qualité) ----
/**
 * La référence se compose de SEGMENTS ordonnés, au choix du laboratoire, suivis
 * du numéro séquentiel (TOUJOURS en dernier — le calcul du prochain numéro en
 * dépend). Chaque segment tire sa valeur d'une source paramétrable :
 *   · prefix      → la pré-référence fixe (multi-sites / multi-entités, ex. BIO24)
 *   · type        → le code du type de document (PR, MO…)
 *   · process     → l'abréviation du processus (PRE, MET…) — cf. écran Processus
 *   · discipline  → le code de la discipline (BIOCH…)
 *   · site        → le code du site (multi-sites)
 * Un segment absent de `segments` n'apparaît pas ; l'ordre du tableau = l'ordre
 * dans la référence.
 */
export type CodifSegment = 'prefix' | 'type' | 'process' | 'discipline' | 'site';
export const CODIF_SEGMENTS: { key: CodifSegment; label: string }[] = [
  { key: 'prefix', label: 'Pré-référence' },
  { key: 'type', label: 'Type de document' },
  { key: 'process', label: 'Processus' },
  { key: 'discipline', label: 'Discipline' },
  { key: 'site', label: 'Site' },
];
/**
 * Défaut : TYPE · PROCESSUS (14/08/2026, décision de la RQ).
 *
 * Il valait « type · discipline » — l'écriture historique, et elle a tenu tant
 * que « Qualité », « Métrologie » et « Pré-analytique » figuraient parmi les
 * disciplines. Ce sont des PROCESSUS, sortis de cette liste le 14/08 : depuis,
 * un document du système de management n'a plus aucune discipline à porter, et
 * l'assistant de demande lui en réclamait une qui n'existait plus. C'est ce que
 * la RQ a signalé — « à la soumission, c'est le processus qu'il faut choisir ».
 *
 * Le codage suit donc l'axe qui décide vraiment : le processus commande le
 * classement du document ET, à défaut de discipline, sa signature
 * (`controleHabilitation`). La discipline reste renseignable — un mode
 * opératoire d'automate en porte une — mais comme famille biologique, pas
 * comme rubrique de classement.
 *
 * ⚠️ Les codes DÉJÀ attribués ne bougent pas : `PR-QUAL-001` reste
 * `PR-QUAL-001`. Une codification n'est pas une donnée, c'est une référence —
 * la recalculer romprait les liens, le journal et les exemplaires papier.
 */
export const DEFAULT_SEGMENTS: CodifSegment[] = ['type', 'process'];

export interface CodifOptions {
  separator?: string; seqPad?: number;
  prefix?: string;
  segments?: CodifSegment[];
  /** valeurs résolues pour le document courant */
  discCode?: string; processCode?: string; siteCode?: string;
}
export const CODIF_DEFAULT = { separator: '-', seqPad: 3, prefix: '', segments: DEFAULT_SEGMENTS };

/* ---- Codification des REGISTRES (NC, actions, réclamations, risques) -------
 * Distincte de celle des documents : un registre ne porte ni type ni discipline,
 * mais un code de registre et une année. Les défauts REPRODUISENT l'ancien
 * format écrit en dur (« BIO24-NC-2026-001 ») — une base existante ne change pas
 * de codification tant que le RQ ne l'édite pas. Miroir de
 * `CODIF_REGISTRES_DEFAUT` (backend, api/ged/controllers/ged.ts) : les deux
 * bougent ensemble, sinon l'aperçu mentirait sur ce que le serveur attribue. */
export const CODIF_REGISTRES_DEFAUT = { prefix: 'BIO24', separator: '-', annee: true, seqPad: 3 };
/** Les registres qui reçoivent une référence automatique, et leur code par défaut.
 *  Cette liste doit couvrir TOUS les tags que le serveur passe à `genRef` /
 *  `createWithRef` — sinon le paramétrage renomme une série que l'écran
 *  n'annonce pas. Recensement du 06/08/2026 : NC, AC, REC, RSK, OPP (ged.ts,
 *  risque.ts) et CAL (calibration.ts, `createWithRef(CAL, 'CAL', …)`) — les
 *  calibrations manquaient ici depuis la livraison du 03/08. */
export const REGISTRES_CODIFIES: { tag: string; label: string }[] = [
  { tag: 'NC', label: 'Non-conformités' },
  { tag: 'AC', label: 'Actions correctives' },
  { tag: 'REC', label: 'Réclamations' },
  { tag: 'RSK', label: 'Risques' },
  { tag: 'OPP', label: 'Opportunités' },
  { tag: 'CAL', label: 'Calibrations' },
  // Lot ISO-5 : `createWithRef(ETA, 'ETA', …)` dans controllers/metrologie.ts.
  { tag: 'ETA', label: 'Étalons et matériaux de référence' },
  // 26/08/2026 : `createWithRef(VER, 'VER', …)` dans controllers/metrologie.ts.
  // Le REGISTRE des vérifications — distinct des étalons : l'un décrit un
  // moyen, l'autre consigne un constat daté avec son verdict.
  { tag: 'VER', label: 'Vérifications métrologiques' },
  // Lot ISO-9 : `createWithRef(…, 'PCA', …)` dans controllers/continuite.ts et
  // `'SUG'` dans controllers/suggestion.ts.
  { tag: 'PCA', label: 'Situations de continuité' },
  { tag: 'SUG', label: 'Suggestions du personnel' },
  // Lot V1 : `genRef(…, 'DVM')` dans controllers/dvm.ts.
  { tag: 'DVM', label: 'Dossiers de validation de méthode' },
  // Lot N4 : `genRef(…, 'MNT')` dans controllers/maintenance.ts.
  { tag: 'MNT', label: 'Plan de maintenance préventive' },
  // Lot G : `genRef(…, 'INV')` dans controllers/inventaire.ts.
  { tag: 'INV', label: 'Missions d’inventaire' },
  /* Recensement du 18/08/2026 — TROIS registres manquaient à l'écran alors que
     le serveur les codifie : leurs références se réglaient donc sans que le
     paramétrage l'annonce, et personne ne pouvait savoir qu'elles suivaient la
     même règle. `qualite-codification` vérifie désormais que cette liste et les
     tags du serveur coïncident, dans les deux sens. */
  { tag: 'OBJ', label: 'Objectifs qualité' },
  { tag: 'INC', label: 'Budgets d’incertitude' },
  { tag: 'EEQ', label: 'Évaluations externes de la qualité' },
];
/** Le PRÉFIXE DE SITE ne s'affiche pas dans l'application.
 *
 *  Décision de la RQ du 18/08/2026 : « le préfixe BIO24 […] lorsqu'on édite un
 *  PDF ou qu'on imprime un document il faut que cela apparaisse, et ce sera
 *  uniquement à ce moment-là — vu que pour le moment BIO24 est monosite. »
 *
 *  Le raisonnement est juste : à l'écran, tout appartient au même laboratoire,
 *  et répéter « BIO24 » sur chaque ligne d'un tableau de cinquante
 *  non-conformités coûte de la place et n'apprend rien. Sur un document qui
 *  SORT — imprimé, envoyé à un confrère, produit en audit —, il devient au
 *  contraire indispensable : rien d'autre ne dit de quel laboratoire vient la
 *  pièce.
 *
 *  ⚠️ La référence STOCKÉE ne change pas. Elle reste complète, c'est elle
 *  l'identité : celle du journal signé, des liens entre dossiers, des URL
 *  partageables. On ne touche qu'à l'AFFICHAGE, et seulement à l'écran.
 *  Retirer le préfixe du stockage rendrait les anciennes références
 *  incomparables aux nouvelles, et casserait tout lien déjà partagé.
 */
export function refCourte(ref: string, prefixe = CODIF_REGISTRES_DEFAUT.prefix): string {
  const r = String(ref || '');
  if (!prefixe) return r;
  // Le séparateur n'est pas supposé : on retire le préfixe ET le caractère qui
  // le suit, quel qu'il soit. Un paramétrage passé de « - » à « / » ne doit pas
  // faire réapparaître le préfixe du jour au lendemain.
  return r.startsWith(prefixe) ? r.slice(prefixe.length + 1) : r;
}

/** Aperçu d'une référence de registre — même assemblage que `genRef` côté serveur. */
export function apercuRefRegistre(
  tag: string,
  opts: { prefix?: string; separator?: string; annee?: boolean; seqPad?: number; code?: string } = {},
  seq = 1,
): string {
  const d = CODIF_REGISTRES_DEFAUT;
  const sep = opts.separator || d.separator;
  const pad = Math.max(1, Math.min(6, opts.seqPad ?? d.seqPad));
  const code = (opts.code || tag).trim() || tag;
  const prefix = typeof opts.prefix === 'string' ? opts.prefix.trim() : d.prefix;
  const parts = [prefix, code, (opts.annee ?? d.annee) ? String(new Date().getFullYear()) : ''].filter(Boolean);
  return `${parts.join(sep)}${sep}${String(seq).padStart(pad, '0')}`;
}
/** Références par défaut des deux organigrammes (documents papier du labo). */
export const ORG_REFS_DEFAUT = { fonctionnel: 'BIO24-A-ORG-01', nominatif: 'BIO24-A-ORG-02' };

/** Construit la référence selon le schéma paramétrable. Le séquentiel est ajouté en dernier. */
export function genCodePreview(type: string, discipline: string, seq = 1, opts: CodifOptions = {}): string {
  const sep = opts.separator ?? CODIF_DEFAULT.separator;
  const pad = Math.max(1, Math.min(6, opts.seqPad ?? CODIF_DEFAULT.seqPad));
  const disc = (opts.discCode && opts.discCode.trim()) ? opts.discCode.trim().toUpperCase() : autoDiscCode(discipline);
  const segs = opts.segments?.length ? opts.segments : DEFAULT_SEGMENTS;
  const valueOf = (s: CodifSegment): string => {
    switch (s) {
      case 'prefix': return (opts.prefix || '').trim().toUpperCase();
      case 'type': return (type || 'MO').toUpperCase();
      case 'process': return (opts.processCode || '').trim().toUpperCase();
      case 'discipline': return disc;
      case 'site': return (opts.siteCode || '').trim().toUpperCase();
      default: return '';
    }
  };
  const parts = segs.map(valueOf).filter(Boolean);
  parts.push(String(seq).padStart(pad, '0'));
  return parts.join(sep);
}

// ---- État de lecture personnel (accusé de réception) ----
/**
 * Où en est la personne connectée vis-à-vis d'un document diffusé ?
 *   · 'lu'      : accusé de réception donné (dateAck) → « Lu le … » ;
 *   · 'a_lire'  : destinataire requis, pas encore d'accusé → « Lecture à accuser » ;
 *   · null      : la personne n'est pas dans la liste de diffusion requise.
 * Rend aussi la date d'accusé pour l'affichage.
 */
/** Seuils d'ancienneté, en jours, au-delà desquels un dossier encore ouvert est
 *  signalé à l'écran. Ils vivent dans le paramétrage (`settings.notif`) parce
 *  que le délai acceptable dépend du laboratoire ; ces valeurs ne servent que
 *  de repli quand la clé n'a jamais été renseignée. */
export const SEUILS_DEFAUT = { ncAncienneteJ: 30, reclReponseJ: 15 };

/** Ce que chaque paragraphe cité à l'écran exige réellement.
 *
 *  Les écrans affichaient « ISO 15189 §8.7 » sans plus : une référence
 *  normative qui n'énonce pas sa propre exigence n'apprend rien à qui ne
 *  connaît pas la norme par cœur — c'est-à-dire à presque tout le monde. */
/**
 * Glose courte d'une clause, pour l'infobulle de `RefISO`.
 *
 * DÉRIVÉE du référentiel unique (`shared/iso15189`) depuis le 29/07 : la table
 * codée ici en dur en était une seconde version, qui avait déjà divergé (elle
 * plaçait les réclamations en §8.6, numérotation de l'édition 2012). Deux
 * sources pour une même vérité normative, c'est la garantie qu'une des deux
 * ment sans qu'on le sache.
 */
export const ISO_CLAUSES: Record<string, string> = Object.fromEntries(
  ISO15189.map((c) => [c.ref, `${c.titre} : ${c.exigence}`]),
);

export function monEtatLecture(
  doc: { diffusion?: { userId: string; requis: boolean; dateAck: string | null }[] | null },
  key: string,
): { etat: 'lu' | 'a_lire'; dateAck: string | null } | null {
  const d = (doc.diffusion || []).find((x) => x.userId === key && x.requis);
  if (!d) return null;
  return d.dateAck ? { etat: 'lu', dateAck: d.dateAck } : { etat: 'a_lire', dateAck: null };
}

// ---- Évaluation & ressources (G3) ----
export const AUDIT_STATES: Record<string, { label: string; tone: string }> = {
  planifie: { label: 'Planifié', tone: 'muted' },
  realise: { label: 'Réalisé', tone: 'info' },
  cloture: { label: 'Clôturé', tone: 'success' },
  // Retiré du programme avant d'avoir eu lieu. Ce n'est pas un audit « non
  // tenu » : le motif est au journal, et l'indicateur l'écarte.
  annule: { label: 'Annulé', tone: 'muted' },
};
export const CONSTAT_TYPES: Record<string, { label: string; tone: string }> = {
  ecart: { label: 'Écart', tone: 'danger' },
  remarque: { label: 'Remarque', tone: 'warning' },
  piste: { label: 'Piste de progrès', tone: 'info' },
};
export const RDD_STATES: Record<string, { label: string; tone: string }> = {
  planifiee: { label: 'Planifiée', tone: 'muted' },
  tenue: { label: 'Tenue', tone: 'info' },
  cloturee: { label: 'Clôturée', tone: 'success' },
};
export const EQUIP_STATES: Record<string, { label: string; tone: string }> = {
  en_service: { label: 'En service', tone: 'success' },
  maintenance: { label: 'En maintenance', tone: 'warning' },
  hors_service: { label: 'Hors service', tone: 'danger' },
  // Dérivé du dossier de mise en service (K1), jamais saisi — voir `etatEquip`.
  mise_en_service: { label: 'En cours de mise en service', tone: 'warning' },
};
/**
 * Familles d'équipements.
 *
 * Les quatre premières suffisaient tant que le parc ne comptait que des
 * automates. Les suivantes sont les **équipements intermédiaires** du registre
 * J2 — pipettes, centrifugeuses, bains-marie, thermomètres, enceintes. Ce ne
 * sont pas des accessoires : la 15189 exige leur raccordement métrologique
 * (§6.4.3, §6.5), et une check-list CQE du laboratoire pose déjà « pipette 1000
 * calibrée » comme critère de conformité d'un passage. Un critère de conformité
 * qui repose sur un instrument dont l'étalonnage n'est pas tracé ne prouve rien.
 *
 * `metrologie: true` marque les familles dont la fiche doit porter le bloc de
 * raccordement — c'est la nature de l'instrument qui le commande, pas une case
 * que l'utilisateur pourrait oublier de cocher.
 */
export const EQUIP_CATEGORIES: { key: string; label: string; metrologie?: boolean }[] = [
  { key: 'automate', label: 'Automate' },
  { key: 'informatique', label: 'Informatique / SIL' },
  /* ⚠️ FAMILLE « LOGICIEL » (05/10/2026) — « dans équipement, je voudrais avoir
     une catégorie pour les logiciels (qui ont un réel impact sur le résultat
     patient comme Boxy ou notre Sperm Class Analyzer ou le logiciel pour le
     risque de T21) » — RQ.
     Elle ne double PAS la nature `logiciel` : elle est COUPLÉE avec elle, et le
     moteur refuse la contradiction (`services/nature-equipement.ts`,
     `incoherenceLogiciel`). Les deux axes répondent à deux questions partout
     ailleurs — « qu'est-ce que c'est » / « qu'est-ce qu'il peut porter » — mais
     pour un logiciel ils répondent la même chose, et deux réponses libres de
     diverger auraient laissé une fiche de famille Logiciel porter des cartes de
     contrôle interne, c'est-à-dire contourner le garde-fou en choisissant
     simplement la famille.
     Sans cette famille, Boxylab et le SCA se cherchaient parmi les 154 fiches
     informatiques, entre les claviers et les souris. */
  { key: 'logiciel', label: 'Logiciel' },
  { key: 'mobilier', label: 'Mobilier & agencement' },
  // ---- équipements intermédiaires (registre J2) ----
  { key: 'pipette', label: 'Pipette / micropipette', metrologie: true },
  { key: 'centrifugeuse', label: 'Centrifugeuse', metrologie: true },
  { key: 'enceinte', label: 'Enceinte thermique (réfrigérateur, congélateur, étuve)', metrologie: true },
  { key: 'bain_marie', label: 'Bain-marie / bloc chauffant', metrologie: true },
  { key: 'thermometre', label: 'Thermomètre / sonde', metrologie: true },
  { key: 'balance', label: 'Balance', metrologie: true },
  { key: 'chronometre', label: 'Chronomètre / minuteur', metrologie: true },
  { key: 'agitateur', label: 'Agitateur / vortex', metrologie: true },
  { key: 'petit_materiel', label: 'Petit matériel', metrologie: true },
  { key: 'metrologie', label: 'Étalon / matériel de référence', metrologie: true },
];

/** Une fiche de cette famille doit porter le suivi métrologique. */
export const exigeMetrologie = (categorie: string) =>
  EQUIP_CATEGORIES.some((c) => c.key === categorie && c.metrologie);

/**
 * LES TROIS NATURES D'ÉQUIPEMENT, dites à l'écran.
 *
 * La famille (`EQUIP_CATEGORIES`) répond « qu'est-ce que c'est ? ». La NATURE
 * répond « qu'est-ce qu'il peut porter ? », et ce n'est pas la même question :
 * un réfrigérateur et un microscope sont tous deux des équipements
 * intermédiaires, mais seul le second produit un résultat.
 *
 * ⚠️ Copie de `LIBELLES_NATURE` (`backend/src/api/ged/services/nature-equipement.ts`).
 * Le socle partagé ne dessert pas le backend : il n'y a pas de source commune à
 * importer, exactement comme pour `CATEGORIES_METROLOGIE`. Un test relit les
 * deux et compare.
 */
export const NATURES_EQUIP: { key: string; titre: string; court: string; aide: string }[] = [
  {
    key: 'mesure',
    titre: 'Équipement de MESURE',
    court: 'mesure',
    aide: 'Il produit un résultat sur un échantillon : lui seul peut porter une carte de contrôle '
      + 'interne et une calibration. Le raccordement métrologique lui est ouvert aussi.',
  },
  {
    key: 'metrologie',
    titre: 'Équipement de MÉTROLOGIE',
    court: 'métrologie',
    aide: 'Il ne rend aucun résultat sur un échantillon, mais la grandeur qu’il tient ou délivre — '
      + 'température d’une enceinte, volume d’une micropipette, vitesse, durée, flux — se raccorde, '
      + 'et ce raccordement est exigé (§6.4.3, §6.5). Raccordement, vérifications intermédiaires et '
      + 'plan de maintenance lui sont ouverts ; la carte de contrôle interne, non — une carte suit la '
      + 'justesse d’un résultat, et il n’y en a pas ici.',
  },
  {
    key: 'accessoire',
    titre: 'Équipement ACCESSOIRE',
    court: 'accessoire',
    aide: 'Il ne rend aucun résultat et ne tient aucune grandeur qui se raccorde — l’ordinateur d’un '
      + 'automate, une imprimante, un onduleur, un clavier. Ni carte de contrôle, ni calibration, ni '
      + 'raccordement : ce qu’il sert porte les trois à sa place.',
  },
  {
    /* ⚠️ QUATRIÈME NATURE (12/09/2026) — « suivi des logiciels comme
       équipement : le SCA, Boxylab… Les logiciels qui pilotent les machines
       doivent être reliés à ces machines. Il y a aussi les logiciels stand
       alone » (RQ, 11/09).
       Une nature plutôt qu'un registre à part : un logiciel a une version, un
       éditeur, un contrat, une mise en service, une fiche de vie et un journal
       — tout ce que la fiche d'équipement porte déjà.
       ⚠️ À ne pas confondre avec le champ `logiciel` d'une fiche, qui porte la
       VERSION EMBARQUÉE de CET appareil (§6.4.2). */
    key: 'logiciel',
    titre: 'LOGICIEL',
    court: 'logiciel',
    aide: 'Ce n’est pas un objet : ni carte de contrôle, ni calibration, ni raccordement. Ce qui le '
      + 'qualifie, c’est sa VERSION, son éditeur, et les appareils qu’il pilote — un logiciel '
      + 'autonome (convertisseur, aide à l’interprétation) n’en pilote aucun, et c’est un cas normal.',
  },
];

/** La nature effective d'une fiche : toute valeur inconnue ou absente vaut
 *  `mesure`, la même règle qu'au serveur — le champ a été ajouté sur un parc
 *  existant et ne doit rien retirer à personne. */
export const natureEquip = (nature?: string | null) =>
  NATURES_EQUIP.find((n) => n.key === String(nature || '').trim()) || NATURES_EQUIP[0];

/**
 * L'appareil produit-il un RÉSULTAT sur un échantillon ?
 *
 * C'est cette question — et non « est-ce un accessoire ? » — qui décide si un
 * appareil peut porter une carte de contrôle interne ou une calibration. Le
 * serveur pose déjà la même règle (`services/nature-equipement.ts`,
 * `porteResultat`) et fait foi ; l'écran la reprend pour ne PROPOSER que ce qui
 * sera accepté.
 *
 * Pourquoi c'est nécessaire : l'écran de saisie du contrôle interne offrait
 * les 247 fiches du parc — pointeuse, claviers, écrans, TPE compris (recette
 * A8). Une liste où 85 % des entrées mènent à un refus n'est pas une liste,
 * c'est un piège ; et le jour où l'une d'elles passe, on surveille la dérive
 * d'un appareil qui ne mesure rien.
 *
 * ⚠️ Doit bouger AVEC `porteResultat` côté serveur — deux règles qui divergent
 * donnent un écran qui propose ce que le moteur refuse.
 */
export const porteResultat = (e: { nature?: string | null }) => natureEquip(e.nature).key === 'mesure';

/** La famille `logiciel` et la nature `logiciel` sont COUPLÉES : le moteur
 *  refuse l'une sans l'autre. L'écran les propose donc ensemble à la saisie —
 *  visiblement, et annulable — plutôt que de laisser découvrir le refus après
 *  l'envoi. Le raisonnement est dans `services/nature-equipement.ts`. */
export const CATEGORIE_LOGICIEL = 'logiciel';
export const estFicheLogiciel = (e: { nature?: string | null; categorie?: string | null }) =>
  natureEquip(e.nature).key === 'logiciel' || String(e.categorie || '').trim() === CATEGORIE_LOGICIEL;

/* ── INTERFAÇAGE AU SIL (05/10/2026) ───────────────────────────────────────
 *
 * « Il peut être relié ou non à notre SIL (d'ailleurs ajoute cela dans les
 *  autres équipements également). » — RQ, 05/10/2026.
 *
 * ⚠️ TROIS ÉTATS, JAMAIS UN BOOLÉEN. Une case vide n'est pas « non » : c'est
 * « personne ne l'a encore dit », et les deux ne se comptent pas ensemble. Avec
 * un booléen, les 248 fiches du parc auraient été déclarées non interfacées du
 * seul fait de l'ajout du champ — une affirmation que personne n'a portée, sur
 * un point qu'un évaluateur vérifie (§7.6).
 *
 * ⚠️ Copie de `SIL_RELIE` / `SIL_SENS` (`backend/src/api/ged/services/nature-equipement.ts`),
 * pour la même raison que `NATURES_EQUIP` : le socle partagé ne dessert pas le
 * backend. Un test relit les deux et compare.
 */
export const SIL_RELIE: { key: string; label: string }[] = [
  // « Sans réponse » EN PREMIER et non en dernier : c'est l'état des 248 fiches
  // du parc, donc la valeur courante de presque toutes — la mettre en fin de
  // liste ferait lire la rubrique comme déjà renseignée à « oui ».
  { key: '', label: 'Sans réponse' },
  { key: 'oui', label: 'Oui — interfacé' },
  { key: 'non', label: 'Non — pas d’interface' },
];

/** Le sens de l'interfaçage, et ce qu'il laisse à la main. C'est la réponse
 *  UTILE : « interfacé » seul ne dit pas où une recopie manuelle subsiste, or
 *  c'est exactement là qu'une erreur de transcription peut naître. */
export const SIL_SENS: Record<string, { label: string; court: string; aide: string }> = {
  bidirectionnel: {
    label: 'Bidirectionnel',
    court: 'SIL ⇄ appareil',
    aide: 'Le SIL descend la liste de travail, l’appareil remonte ses résultats. Aucune recopie manuelle.',
  },
  vers_sil: {
    label: 'Remontée des résultats seule (appareil → SIL)',
    court: 'appareil → SIL',
    aide: 'Les résultats remontent seuls, mais la liste de travail se saisit à la main sur l’appareil : '
      + 'c’est là qu’une erreur d’identification du patient peut naître.',
  },
  depuis_sil: {
    label: 'Descente de la demande seule (SIL → appareil)',
    court: 'SIL → appareil',
    aide: 'L’appareil reçoit la demande, mais ses résultats sont recopiés à la main dans le SIL : '
      + 'c’est là qu’une erreur de transcription peut naître.',
  },
};

export const SENS_SIL = Object.keys(SIL_SENS);

/** Comment la traçabilité au SI est assurée — c'est la question du §6.5. */
export const RACCORDEMENTS: Record<string, { label: string; tone: string; aide: string }> = {
  accredite: { label: 'Organisme accrédité', tone: 'success', aide: 'Étalonnage par un laboratoire accrédité — raccordement au SI démontré.' },
  constructeur: { label: 'Constructeur', tone: 'info', aide: 'Étalonnage par le fabricant. Vérifier que son certificat établit le raccordement.' },
  interne: { label: 'Interne, par étalon raccordé', tone: 'info', aide: 'Vérification interne au moyen d’un étalon lui-même raccordé — l’étalon doit avoir sa propre fiche.' },
  aucun: { label: 'Non établi', tone: 'danger', aide: 'Aucun raccordement : l’instrument ne peut pas servir de critère de conformité.' },
};

/* ---- Chaîne de raccordement (§6.5, lot ISO-5) ---------------------------- */

/**
 * ⚠️ LES TROIS ÉTATS DU RACCORDEMENT — jamais additionnés.
 *
 * « Raccordé mais sans pièce » n'est pas une nuance de « raccordé » : c'est
 * l'écart d'audit lui-même. Un compteur qui ferait « raccordés : 18 » en
 * fondant les deux premiers serait exactement le chiffre qui rassure un
 * laboratoire jusqu'au jour de la visite. Les libellés le disent en toutes
 * lettres pour que personne ne soit tenté de les regrouper à l'affichage.
 */
export const ETATS_RACCORDEMENT: Record<string, { label: string; tone: string; aide: string }> = {
  raccorde_documente: {
    label: 'Raccordé et documenté', tone: 'success',
    aide: 'Chaque maillon porte son certificat, et il est encore valable : le raccordement se démontre.',
  },
  raccorde_sans_piece: {
    label: 'Raccordé, non documenté', tone: 'warning',
    aide: 'Un raccordement est affirmé, mais un maillon au moins n’est ni prouvé ni valide. C’est l’écart que l’évaluateur relève.',
  },
  non_raccorde: {
    label: 'Non raccordé', tone: 'danger',
    aide: 'Aucun raccordement n’est même prétendu : l’instrument ne peut pas servir de critère de conformité.',
  },
  // État propre au REGISTRE DES ÉTALONS : un certificat expiré n'est ni
  // « documenté » ni « déclaré » — il l'a été, et ne l'est plus.
  perime: {
    label: 'Certificat périmé', tone: 'danger',
    aide: 'Le certificat existe mais n’engage plus : cet étalon ne raccorde plus rien.',
  },
};

/** L'état d'un maillon pris isolément. `sans_objet` n'est pas un défaut : quand
 *  un organisme accrédité mesure avec SES étalons, exiger un étalon du
 *  laboratoire ferait apparaître une chaîne rompue sur les instruments les
 *  mieux raccordés. */
export const ETATS_MAILLON: Record<string, { label: string; tone: string }> = {
  documente: { label: 'Documenté', tone: 'success' },
  declare: { label: 'Déclaré, sans pièce', tone: 'warning' },
  perime: { label: 'Périmé', tone: 'danger' },
  manquant: { label: 'Maillon manquant', tone: 'danger' },
  sans_objet: { label: 'Sans objet', tone: 'muted' },
};

/** Ce que porte le registre des étalons — trois choses que le §6.5 ne confond
 *  pas, et qu'il ne faut donc pas fondre en une seule ligne. */
export const NATURES_ETALON: Record<string, { label: string; aide: string }> = {
  etalon_reference: {
    label: 'Étalon de référence',
    aide: 'Porte une valeur raccordée et sert à étalonner un instrument.',
  },
  mrc: {
    label: 'Matériau de référence certifié (MRC)',
    aide: 'Valeur assignée avec son incertitude, délivrée par un producteur — il raccorde une méthode, pas un instrument.',
  },
  materiau_verification: {
    label: 'Matériau de vérification',
    aide: 'Sert à contrôler qu’on n’a pas dérivé, sans prétendre raccorder quoi que ce soit.',
  },
};

/** Le dernier maillon : ce à quoi tout aboutit. */
export const RATTACHEMENTS_METRO: Record<string, { label: string; aide: string }> = {
  national: { label: 'Institut national de métrologie', aide: 'Le raccordement remonte à l’institut national de métrologie d’un pays (LNE, PTB, NIST, INRAP…).' },
  international: { label: 'Arrangement international', aide: 'Reconnaissance mutuelle entre organismes d’accréditation (ILAC MRA, EA MLA) ou CIPM MRA.' },
  inconnu: { label: 'Non renseigné', aide: 'On ne sait pas à quelle référence reconnue cet étalonnage aboutit : la chaîne s’arrête avant son dernier maillon.' },
};

export const ETALON_STATUTS: Record<string, { label: string; tone: string }> = {
  en_service: { label: 'En service', tone: 'success' },
  retire: { label: 'Retiré du service', tone: 'muted' },
};

/** Ce que la dernière opération a conclu. */
export const CONSTATS_METRO: Record<string, { label: string; tone: string }> = {
  conforme: { label: 'Conforme', tone: 'success' },
  ajuste: { label: 'Ajusté puis conforme', tone: 'info' },
  non_conforme: { label: 'Non conforme', tone: 'danger' },
};
export const INTERV_TYPES = [
  { key: 'maintenance', label: 'Maintenance' },
  { key: 'etalonnage', label: 'Étalonnage' },
  { key: 'verification', label: 'Vérification' },
  { key: 'reparation', label: 'Réparation' },
];
export const FOURN_TYPES = [
  { key: 'reactifs', label: 'Réactifs' },
  { key: 'consommables', label: 'Consommables' },
  { key: 'materiel', label: 'Matériel / équipement' },
  { key: 'metrologie', label: 'Métrologie / étalonnage' },
  { key: 'prestation', label: 'Prestation / sous-traitance' },
  // 19/08/2026 — deux natures qui ne sont PAS des fournisseurs et ont leur
  // propre onglet : les confrères (prêt/emprunt, clients de sous-traitance) et
  // les partenaires étrangers auxquels NOUS sous-traitons (Cerba, Biomnis).
  { key: 'confrere', label: 'Confrère (laboratoire)' },
  { key: 'soustraitance', label: 'Sous-traitance étrangère' },
];
export const FOURN_STATES: Record<string, { label: string; tone: string }> = {
  actif: { label: 'Référencé', tone: 'success' },
  surveillance: { label: 'Sous surveillance', tone: 'warning' },
  suspendu: { label: 'Suspendu', tone: 'danger' },
};
export const EVAL_CRITERES = [
  { key: 'qualite', label: 'Qualité produit' },
  { key: 'delais', label: 'Respect des délais' },
  { key: 'conformite', label: 'Conformité documentaire' },
  { key: 'reactivite', label: 'Réactivité / SAV' },
];
export const LOT_STATES: Record<string, { label: string; tone: string }> = {
  quarantaine: { label: 'En quarantaine', tone: 'warning' },
  accepte: { label: 'Accepté', tone: 'info' },
  en_service: { label: 'En service', tone: 'success' },
  epuise: { label: 'Épuisé', tone: 'neutral' },
  retire: { label: 'Retiré / non conforme', tone: 'danger' },
};
export const CQI_STATES: Record<string, { label: string; tone: string }> = {
  conforme: { label: 'Sous contrôle', tone: 'success' },
  alerte: { label: 'Alerte Westgard', tone: 'warning' },
  rejet: { label: 'Hors contrôle', tone: 'danger' },
};
/** Multirègles de Westgard — ce que chaque règle signale au RQ (infobulle).
 *  Le verdict seul ne suffit pas : une erreur aléatoire (R-4s) et une dérive
 *  systématique (4-1s, 10-x) n'appellent pas le même traitement. */
export const WESTGARD_RULES: Record<string, string> = {
  '1-2s': 'Avertissement : un point au-delà de ±2s. Examiner les règles de série.',
  '1-3s': 'Rejet : un point au-delà de ±3s — erreur aléatoire importante.',
  '2-2s': 'Rejet : deux points consécutifs au-delà du même ±2s — erreur systématique.',
  'R-4s': 'Rejet : deux points consécutifs de part et d’autre, écart > 4s — erreur aléatoire.',
  '4-1s': 'Rejet : quatre points consécutifs au-delà du même ±1s — dérive.',
  '10-x': 'Rejet : dix points consécutifs du même côté de la cible — biais.',
};

/** Points d'un CQI {v, ts, comment} alignés sur `valeurs` : les séries
 *  antérieures au champ `points` sont reconstituées (valeur seule). */
export function cqiPoints(q: { valeurs?: number[] | null; points?: { v: number; ts: string; comment?: string }[] | null }): { v: number; ts: string; comment: string }[] {
  const valeurs = q.valeurs || [];
  const pts = q.points || [];
  if (pts.length === valeurs.length) return pts.map((p) => ({ v: Number(p.v), ts: p.ts || '', comment: p.comment || '' }));
  return valeurs.map((v, i) => ({ v: Number(v), ts: pts[i]?.ts || '', comment: pts[i]?.comment || '' }));
}

/** Droits CQI/CQE/calibration accordés d'origine — miroir de `GED_PERMS_DEFAULT`
 *  (backend/src/ged-seed.ts) et de `DROITS_DEFAUT` (controllers/ged.ts). Repli
 *  quand `settings.perms` ne porte pas la clé : une base antérieure au chantier
 *  du 28/07/2026 n'a que les six droits documentaires. */
const DROITS_DEFAUT: Record<string, string[]> = {
  // R10 (07/10/2026) : `cqiApprouverCible` (figer la cible interne, repli
  // `cqiValider`) et `cqiRapporter` (exports CQ, repli `imprimer`).
  superadmin: ['cqiSaisir', 'cqiValider', 'cqiDeroger', 'cqiParametrer', 'cqiRegles', 'cqiApprouverCible', 'cqiRapporter', 'cqeSaisir', 'cqeDeroger', 'cqeRevoirAction', 'calibrationSaisir', 'imprimer'],
  manager: ['cqiSaisir', 'cqiValider', 'cqiDeroger', 'cqiParametrer', 'cqiRegles', 'cqiApprouverCible', 'cqiRapporter', 'cqeSaisir', 'cqeDeroger', 'cqeRevoirAction', 'calibrationSaisir', 'imprimer'],
  technician: ['cqiSaisir', 'cqiValider', 'cqeSaisir', 'calibrationSaisir', 'imprimer'],
  partner: [],
  public: [],
};

/**
 * Ce que l'aperçu officiel a le droit de montrer — MIROIR de `etatApercu()`
 * (backend/src/api/ged/controllers/apercu.ts). L'écran masque, le serveur
 * refuse : les deux doivent dire la même chose, sinon on propose un bouton qui
 * échoue.
 *
 * Un document en rédaction n'a pas d'aperçu : on le travaille dans son fichier.
 */
/** L'aperçu montre-t-il le fichier TEL QUEL (sans cartouche ni filigrane) ? Externe
 *  (type DE) ou fichier sans injection possible (PDF, présentation) — pas « importé »
 *  seul, qui ne dit que le format (RQ, 04/10/2026). MIROIR de `apercuTelQuel()` (apercu.ts). */
export function apercuTelQuel(doc: { classe?: unknown; docType?: unknown; fichier?: unknown }): boolean {
  if (String(doc.classe || '') !== 'importe') return false;
  const externe = String(doc.docType || '').trim().toUpperCase() === 'DE';
  const ext = String((doc.fichier as { ext?: unknown } | null)?.ext || '').toLowerCase();
  return externe || ext === 'pdf' || ext === 'pptx';
}

/**
 * Les statuts où une version APPLICABLE coexiste avec une version candidate.
 * ⚠️ `retire` en est ABSENT à dessein — un document retiré n'est applicable à
 * aucun titre. MIROIR de `STATUTS_REVISION_EN_COURS` (`apercu.ts`).
 */
export const STATUTS_REVISION_EN_COURS = ['enrevision', 'averifier', 'aapprouver'];

export function etatApercu(
  statut: string,
  classe?: string,
  contexte?: { revisionEnCours?: boolean; versionDemandee?: 'applicable' | 'candidate' | 'obsolete' } | null,
): { visible: boolean; imprimable: boolean; raison: string } {
  /* ── UNE VERSION OBSOLÈTE SE RELIT, ELLE NE S'APPLIQUE PLUS ──────────────
     Depuis l'archivage des versions, l'onglet Versions sert n'importe quelle
     version passée : visible (elle faisait foi pour les dossiers jugés sous son
     empire), jamais imprimable, filigrane « NE PAS APPLIQUER ». AVANT la
     branche du document externe, qui autorise l'impression de tout ce qui n'est
     pas `statut === 'obsolete'` — ici c'est la VERSION qui l'est, pas le
     document. MIROIR de `apercu.ts`. */
  if (contexte?.versionDemandee === 'obsolete') {
    return { visible: true, imprimable: false, raison: 'Version obsolète — remplacée : elle ne s’applique plus.' };
  }
  /* ── LA VERSION APPLICABLE D'ABORD (RQ, 06/10/2026) ──────────────────────
     Tant qu'on sert la version APPLICABLE, son aperçu vaut exactement celui
     d'un document en vigueur : visible, imprimable, sans filigrane — même si
     une révision est en chantier à côté. Le candidat, lui, garde les règles de
     son étape (branches ci-dessous). Voir `lib/version-en-cours.ts`. */
  if (contexte?.revisionEnCours && contexte.versionDemandee !== 'candidate'
      && STATUTS_REVISION_EN_COURS.includes(statut)) {
    return { visible: true, imprimable: true, raison: '' };
  }
  /* ── Document EXTERNE : l'aperçu, c'est l'EXEMPLAIRE ─────────────────────
     La règle « pas d'aperçu tant que le document est en rédaction » protège
     d'un brouillon d'allure officielle qui circulerait. Elle n'a aucun sens
     pour un référentiel acheté : le laboratoire n'en rédige pas une ligne, il
     n'y a pas de brouillon, et rien à tamponner — on ne pose pas le cartouche
     de bio24 sur la norme de quelqu'un d'autre.
     Un document externe est donc consultable dès qu'un exemplaire est déposé,
     quel que soit son statut. */
  /* ── Aperçu VISIBLE mais NON APPLICABLE en circuit (RQ, 04/10/2026) ──────
     Le 18/08 l'aperçu était MASQUÉ pendant la revue : il avait l'allure exacte du
     document opposable. La RQ veut désormais le voir — mais GRISÉ, pour qu'on
     comprenne au premier regard qu'il est en cours de validation. Les deux
     protections de 18/08 restent : il ne se télécharge ni ne s'imprime
     (`imprimable: false`), et chaque page porte le filigrane « EN COURS DE
     VALIDATION — NE PAS APPLIQUER » (voir `cartoucheDe`). L'écran le grise.
     ⚠️ AVANT la branche « importé » : `classe` dit le FORMAT (un mode opératoire
     rédigé ici puis déposé en Word est « importé »), pas l'origine. Un document
     en vérification ou en approbation a un circuit, donc il est rédigé ici ; le
     laisser passer par la branche externe le rendait imprimable en pleine revue. */
  if (statut === 'averifier') {
    return { visible: true, imprimable: false, raison: 'Document rédigé, en cours de vérification : il n’est pas encore applicable.' };
  }
  if (statut === 'aapprouver') {
    return { visible: true, imprimable: false, raison: 'Document rédigé et vérifié, en attente d’approbation : il n’est pas encore applicable.' };
  }
  if (classe === 'importe') {
    return { visible: true, imprimable: statut !== 'obsolete', raison: '' };
  }
  if (statut === 'brouillon' || statut === 'enrevision') {
    return { visible: false, imprimable: false, raison: 'Le contenu se travaille dans l’onglet Fichier. L’aperçu officiel apparaîtra à la mise en vérification.' };
  }
  /* ── Pas d'aperçu OFFICIEL avant la diffusion (RQ, 18/08/2026) ──────────
     L'aperçu porte le cartouche réglementaire : code, version, signataires,
     date de vigueur. Il a donc l'apparence exacte du document opposable. Le
     rendre dès la mise en vérification permettait d'en tirer un PDF d'allure
     officielle pour un texte que personne n'avait encore approuvé — et rien,
     sur la page imprimée, ne distinguait ce PDF du vrai autrement que par une
     mention que l'œil saute.
     Vérificateur et approbateur ne perdent RIEN : ils lisent et annotent le
     document dans l'onglet Fichier, qui est fait pour cela. Ce qui disparaît,
     c'est la version tamponnée — celle qui circule. */
  if (statut === 'envigueur') return { visible: true, imprimable: true, raison: '' };
  if (statut === 'retire') return { visible: true, imprimable: false, raison: 'Document retiré d’urgence.' };
  return { visible: true, imprimable: false, raison: 'Version obsolète — remplacée.' };
}

/** Le profil a-t-il ce droit ? Le paramétrage fait foi (décision D6).
 *  N'affiche/masque QUE l'interface : le serveur revalide systématiquement. */
export function peut(
  perms: Record<string, Record<string, boolean>> | undefined,
  profile: string,
  action: string,
): boolean {
  const duProfil = perms?.[profile];
  if (duProfil && typeof duProfil[action] === 'boolean') return duProfil[action];
  return (DROITS_DEFAUT[profile] || []).includes(action);
}

/**
 * LE CATALOGUE DES DROITS DU CONTRÔLE QUALITÉ (lot E, 09/10/2026) — ce que
 * Paramétrage → Droits affiche enfin. Ces clés existaient depuis le 28/07/2026
 * dans `settings.perms` et n'avaient AUCUN écran : elles ne se réglaient qu'en
 * base. Les libellés disent l'ACTE ; `repli` nomme le droit ancien qui répond
 * quand la clé neuve est absente (R10). Miroir des défauts : `DROITS_DEFAUT`
 * ci-dessus, `GED_PERMS_DEFAULT` (ged-seed.ts), `DROITS_DEFAUT` (controllers/ged.ts).
 */
export const DROITS_CQ: { cle: string; libelle: string; aide: string; famille: string; repli?: string }[] = [
  { famille: 'Contrôle interne (CQI)', cle: 'cqiSaisir', libelle: 'Saisir un passage', aide: 'Enregistrer une série, importer un rapport, renseigner la conduite tenue' },
  { famille: 'Contrôle interne (CQI)', cle: 'cqiValider', libelle: 'Valider un passage', aide: 'Validation manuelle d’un avertissement, décision et clôture d’une conduite tenue' },
  { famille: 'Contrôle interne (CQI)', cle: 'cqiDeroger', libelle: 'Déroger', aide: 'Accepter un rejet avec dérogation, saisir sur un lot périmé avec motif' },
  { famille: 'Contrôle interne (CQI)', cle: 'cqiParametrer', libelle: 'Paramétrer une carte', aide: 'Règles actives, cible, lot de contrôle, TEa, création et archivage d’une carte' },
  { famille: 'Contrôle interne (CQI)', cle: 'cqiRegles', libelle: 'Régler le catalogue des règles', aide: 'Rubrique « Contrôle interne » du paramétrage : règles de Westgard et proposition de NC' },
  { famille: 'Contrôle interne (CQI)', cle: 'cqiApprouverCible', libelle: 'Approuver une cible interne', aide: 'Fige la moyenne et l’écart-type observés comme cible du lot — acte lourd', repli: 'cqiValider' },
  { famille: 'Contrôle interne (CQI)', cle: 'cqiRapporter', libelle: 'Exporter l’audit et les rapports CQ', aide: 'CSV, Excel, PDF de l’audit et de la synthèse mensuelle', repli: 'imprimer' },
  { famille: 'Évaluation externe (EEQ)', cle: 'cqeSaisir', libelle: 'Saisir un envoi ou un résultat', aide: 'Programmes, cycles, envois, z-scores' },
  { famille: 'Évaluation externe (EEQ)', cle: 'cqeDeroger', libelle: 'Déroger', aide: 'Envoyer malgré un CQI non conforme' },
  { famille: 'Évaluation externe (EEQ)', cle: 'cqeRevoirAction', libelle: 'Revoir une action corrective', aide: 'La revue de l’action corrective EEQ — pas le même acte que la dérogation' },
  { famille: 'Calibrations et impression', cle: 'calibrationSaisir', libelle: 'Déclarer une calibration', aide: 'Depuis un point CQI ou le registre des calibrations' },
  { famille: 'Calibrations et impression', cle: 'imprimer', libelle: 'Imprimer', aide: 'Impressions et exports courants' },
];

/** Un droit RÉCENT avec son droit de REPLI (R10) — miroir de `peutDroitOuRepli`
 *  (controllers/ged.ts) : la clé neuve décide si le paramétrage la porte,
 *  sinon le droit ancien répond. L'écran masque, le serveur refuse : les deux
 *  doivent dire la même chose. */
export function peutOuRepli(
  perms: Record<string, Record<string, boolean>> | undefined,
  profile: string,
  action: string,
  repli: string,
): boolean {
  const duProfil = perms?.[profile];
  if (duProfil && typeof duProfil[action] === 'boolean') return duProfil[action];
  return peut(perms, profile, repli);
}

/** Peut accorder une dérogation CQI. Conservée comme raccourci lisible ; elle lit
 *  désormais le paramétrage au lieu de figer manager/superadmin. */
export function estBiologiste(
  profile: string,
  perms?: Record<string, Record<string, boolean>>,
): boolean {
  return peut(perms, profile, 'cqiDeroger');
}

/** Couleurs de niveau pour le graphe multi-niveaux (Levey-Jennings superposé). */
export const CQI_LEVEL_COLORS = ['var(--cyan-600)', 'var(--violet-600, #7C3AED)', 'var(--amber-600)', 'var(--green-600)', 'var(--blue-600)', 'var(--rose-600, #E11D74)'];

export const EEQ_RESULTS: Record<string, { label: string; tone: string }> = {
  satisfaisant: { label: 'Satisfaisant', tone: 'success' },
  surveiller: { label: 'À surveiller', tone: 'warning' },
  non_satisfaisant: { label: 'Non satisfaisant', tone: 'danger' },
};
/**
 * Jours restants avant une date (négatif = échéance dépassée).
 *
 * ── Pourquoi on compare des JOURS et jamais des instants ────────────────────
 *
 * L'ancienne version soustrayait `Date.now()` de minuit UTC puis arrondissait
 * vers le bas. Une non-conformité déclarée le matin même affichait donc
 * « ouverte depuis 1 j » dès l'après-midi : `2026-08-08T00:00Z − 14 h` donne
 * −0,58 jour, que `Math.floor` ramène à −1 (recette A9). Le même biais
 * annonçait une échéance « dépassée » le jour où elle était due.
 *
 * On ramène donc les deux bornes à MINUIT LOCAL avant de soustraire — c'est la
 * règle déjà tenue côté serveur (« tout se compare en jours, jamais en
 * instants », `plan-maintenance.ts`). Aujourd'hui rend 0, demain 1, hier −1,
 * quelle que soit l'heure qu'il est.
 */
export function joursRestants(date?: string | null): number | null {
  if (!date) return null;
  // `new Date('2026-08-08')` est interprété en UTC, `…T00:00:00` en LOCAL :
  // c'est la seconde lecture qu'il faut, pour rester dans le même fuseau que
  // le minuit d'aujourd'hui calculé juste en dessous.
  const jour = String(date).slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(jour)) return null;
  const cible = new Date(`${jour}T00:00:00`).getTime();
  if (Number.isNaN(cible)) return null;
  const maintenant = new Date();
  const aujourdhui = new Date(maintenant.getFullYear(), maintenant.getMonth(), maintenant.getDate()).getTime();
  // `round` et non `floor` : le passage à l'heure d'été décale une des deux
  // bornes d'une heure, ce qui suffirait à faire perdre un jour entier.
  return Math.round((cible - aujourdhui) / 86400000);
}
/** Jours écoulés depuis une date (positif = dans le passé). */
export function joursDepuis(date?: string | null): number | null {
  const r = joursRestants(date);
  return r === null ? null : -r;
}
/** Échéance dépassée ? */
export function estEnRetard(date?: string | null): boolean {
  const j = joursRestants(date);
  return j !== null && j < 0;
}

export type NiveauLot = 'perime' | 'critique' | 'surveiller' | null;
/** Niveau d'alerte d'un lot de réactif.
 *
 *  L'ancien `lotAlert` renvoyait UN booléen pour « moins de 60 jours », borne
 *  incluant les écarts négatifs : un lot périmé depuis trois mois s'affichait
 *  donc exactement comme un lot utilisable encore huit semaines. Trois niveaux
 *  distincts, parce que les gestes attendus diffèrent — retirer, commander,
 *  surveiller. */
export function lotNiveau(datePeremption?: string | null): NiveauLot {
  const j = joursRestants(datePeremption);
  if (j === null) return null;
  if (j < 0) return 'perime';
  if (j <= 30) return 'critique';
  if (j <= 60) return 'surveiller';
  return null;
}
const LOT_NIVEAU_META: Record<Exclude<NiveauLot, null>, { label: (j: number) => string; fg: string; bg: string; rang: number }> = {
  perime: { label: (j) => `Périmé depuis ${Math.abs(j)} j`, fg: 'var(--red-600)', bg: 'var(--red-50)', rang: 0 },
  critique: { label: (j) => `Périme dans ${j} j`, fg: 'var(--red-600)', bg: 'var(--red-50)', rang: 1 },
  surveiller: { label: (j) => `Périme dans ${j} j`, fg: 'var(--amber-700, #8A5B00)', bg: 'var(--amber-50, #FFF8EC)', rang: 2 },
};
export function lotNiveauMeta(niveau: Exclude<NiveauLot, null>, jours: number) {
  const m = LOT_NIVEAU_META[niveau];
  return { label: m.label(jours), fg: m.fg, bg: m.bg, rang: m.rang };
}
/** Conservé pour le compte global « il y a quelque chose à regarder ». */
export function lotAlert(datePeremption?: string | null): boolean {
  return lotNiveau(datePeremption) !== null;
}
